import React, { useEffect, useState, useRef } from "react";
import {
    TableHead,
    TableBody,
    Table,
    TableContainer,
    TableCell,
    TableRow,
    Box,
    Pagination,
    Button,
    FormControl,
    Autocomplete,
    TextField,
    Typography,
    Grid,
    Card,
    CardContent,
    Paper,
    TablePagination,
    IconButton,
    InputLabel,
    Select,
    MenuItem,
} from "@mui/material";
import { commonRunQuery } from "../../util/API/loans";
import { PieChart, Pie, Cell, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend } from "recharts";
import "./LoanUserAgeAnalysisTab.scss";
import { styled } from "@mui/material/styles";
import { tableCellClasses } from "@mui/material/TableCell";
import pdfImage from "../../images/pdf-image.svg"
import { CSVLink } from "react-csv";
import moment from "moment";
import CSVImage from "../../images/csv-file.png";
import jsPDF from "jspdf";
import "jspdf-autotable";
import autoTable from "jspdf-autotable";

const LoanUserAgeAnalysisTab = ({ selectedUser }) => {

    const [ageAnalysisData, setAgeAnalysisData] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);

    const StyledTableCell = styled(TableCell)(({ theme }) => ({
        [`&.${tableCellClasses.head}`]: {
            backgroundColor: "rgba(0,0,0, .1)",
            color: theme.palette.common.black,
            fontWeight: "bold",
            textAlign:"center",
        },
        [`&.${tableCellClasses.body}`]: {
            fontSize: 25,
            fontWeight: "600",
        },
    }));

    const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#A28CFF", "#FF6666"];

    const processChartData = (data) => {
        const ageBracketCounts = {};
    
        data.forEach((row) => {
            const ageBracket = row?.record?.age_bracket;
    
            if (ageBracket) {
                ageBracketCounts[ageBracket] = (ageBracketCounts[ageBracket] || 0) + 1;
            }
        });
    
        return Object.entries(ageBracketCounts).map(([name, value]) => ({ name, value }));
    };

    const loanAgeChartData = processChartData(ageAnalysisData);

    useEffect(()=> {
        getLoanAgeAnalysisData();
    },[]);

    const getLoanAgeAnalysisData = async()=> {
        try {
            const response = await commonRunQuery(246, selectedUser);
            console.log("getLoanAgeAnalysisData_response================",response);
            if(response?.data?.header?.callStatus === "1" && response?.data?.header?.reccount !== "0") {
                const ageAnalysisDataArray = Array.isArray(response?.data?.body?.record) ? response?.data?.body?.record : [response?.data?.body?.record];
                console.log("ageAnalysisDataArray================",ageAnalysisDataArray);
                setAgeAnalysisData(ageAnalysisDataArray);
            } else {
                setAgeAnalysisData([]);
            }
        } catch (error) {
            console.log("getLoanAgeAnalysisData_error================",error);
        }
    }

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const titleRow = [
        `Age Analysis Report`,
    ];

    const csvData = [
        // Add the title row as the first row
        titleRow,
        // Add an empty row to separate the title from the header
        [],
        //These will be header columns
        [
            'Age Analysis Bracket',
            'Days Overdue',
            'Issued Amount',
            'Issued Date',
            'Due Date',
            'Outstanding Principle',
            'Outstanding Late Fees',
            'Total Amount',            
            'Total Amount Applied'            
        ],

        // Add the data rows
        ...ageAnalysisData.map((row) => [
            row?.record?.age_bracket,
            row?.record?.days_overdue,
            row?.loanissued?.issuedamount,
            row?.loanissued?.issueddate,
            row?.loanissued?.loanduedate,
            row?.loanissued?.outstandingprinciple,
            row?.loanissued?.outstandinglatefees,
            row?.loanissued?.totalamount,
            row?.loanissued?.totalamountapplied
        ]),
    ];

    const runPDF = () => {
        const doc = new jsPDF();
        const title = "Loan Status Report";
    
        // Title
        doc.setFontSize(18);
        doc.text(title, 14, 22);
    
        // Optional: Filter Info
        // const filters = ``;
        // doc.setFontSize(11);
        // doc.text(filters, 14, 30);
    
        // Table Header
        const headers = [
            [
                'Status',
                'Disbursement Amount',
                'Disbursement Type',
                'Issued Amount',
                'Issued Date',
                'Due Date',
                'Outstanding Principle',
                'Outstanding Late Fees',
                'Total Amount',
                'Total Amount Applied'
            ]
        ];
    
        // Table Body
        const data = ageAnalysisData.map((row) => [
            row?.loanissued?.fullypaidind == "0" ? "Not Fully Paid" : row?.loanissued?.fullypaidind == "1" ? "Fully Paid" : "Cancelled",
            row?.loanissued?.disbursementamount,
            row?.loanissued?.disbursementtype,
            row?.loanissued?.issuedamount,
            row?.loanissued?.issueddate,
            row?.loanissued?.loanduedate,
            row?.loanissued?.outstandingprinciple,
            row?.loanissued?.outstandinglatefees,
            row?.loanissued?.totalamount,
            row?.loanissued?.totalamountapplied
        ]);
    
        // Generate PDF Table
        autoTable(doc, {
            head: headers,
            body: data,
            startY: 38,
            styles: {
                fontSize: 8,
                cellPadding: 2,
            },
            headStyles: {
                fillColor: [22, 160, 133],
            }
        });
    
        // Save PDF
        doc.save("LoanDueReport.pdf");
    };

    return (
        <Box className="user-age-analysis-container">
            <Typography variant="h5" sx={{ mb: 2 }}>Loan Age Analysis</Typography>
                
            {/* Pie Chart Container */}
            {ageAnalysisData && ageAnalysisData?.length>0 && <Box sx={{ display: "flex", justifyContent: "center", my: 3 }}>
                <Grid container spacing={3} className="m-0" >
                    <Card className="grid-cards">
                        <CardContent 
                            sx={{
                                transition: "transform 0.3s ease-in-out, filter 0.3s ease-in-out",
                                "&:hover": {
                                    transform: "scale(1.02)", 
                                    filter: "drop-shadow(5px 5px 10px rgba(0,0,0,0.2))", // Adds a shadow
                                },
                            }}
                        >
                            <PieChart width={300} height={300}>
                                <Pie 
                                    data={loanAgeChartData} 
                                    cx="50%" 
                                    cy="40%" 
                                    outerRadius={80} 
                                    fill="#8884d8" 
                                    dataKey="value"
                                    label
                                >
                                    {loanAgeChartData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip />
                                <Legend verticalAlign="bottom" align="center" iconType="circle" />
                            </PieChart>
                        </CardContent>
                    </Card> 
                </Grid>
            </Box>}
            <Box>
                <Box className="d-flex justify-content-end">
                    {/* <Typography sx={{ display:"flex", alignItems:"flex-end" }} variant="h6">Loan Requests</Typography> */}
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Box>
                            <CSVLink
                                data={csvData}
                                filename={`LoanAgeAnalysisReport.csv`}
                            >
                                <IconButton
                                    sx={{
                                        width: 40,
                                        height: 40,
                                        color: "#6c757d",
                                        "&:hover": {
                                        backgroundColor: "rgba(108, 117, 125, 0.1)",
                                        },
                                    }}
                                >
                                    <img src={CSVImage} style={{ width:"48px" }} loading="lazy" />
                                </IconButton>
                            </CSVLink>{" "}
                        </Box>

                        <Box>
                            <IconButton
                                onClick={runPDF}
                                sx={{
                                    width: 40,
                                    height: 40,
                                    color: "#6c757d",
                                    backgroundColor: "transparent",
                                    "&:hover": {
                                        backgroundColor: "rgba(108, 117, 125, 0.1)",
                                    },
                                }}
                            >
                                <img src={pdfImage} alt="PDF" loading="lazy" />
                            </IconButton>
                        </Box>
                    </Box>
                    {/* <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <Box>
                            <CSVLink
                                data={csvData}
                                filename={`LoanAgeAnalysisReport.csv`}
                            >
                                <img src={CSVImage} className="excel-icon" />
                            </CSVLink>{" "}
                        </Box>
                    </Box> */}
                </Box>

                

                <TableContainer className="bordered-table mt-2">
                    <Table>
                        <TableHead>
                            <TableRow>
                                <StyledTableCell>Age Analysis Bracket</StyledTableCell>
                                <StyledTableCell>Days Overdue</StyledTableCell>
                                <StyledTableCell>Issued Amount</StyledTableCell>
                                <StyledTableCell>Issued Date</StyledTableCell>
                                <StyledTableCell>Due Date</StyledTableCell>
                                {/* <StyledTableCell>Disbursement Amount</StyledTableCell> */}
                                <StyledTableCell>Outstanding Principle</StyledTableCell>
                                <StyledTableCell>Outstanding Late Fees</StyledTableCell>
                                <StyledTableCell>Total Amount</StyledTableCell>
                                <StyledTableCell>Total Amount Applied</StyledTableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {ageAnalysisData && ageAnalysisData.length > 0 ? ageAnalysisData
                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                .map((row, index) => (
                                    <TableRow key={index}>
                                        <TableCell align="center">{row?.record?.age_bracket}</TableCell>
                                        <TableCell align="center">{row?.record?.days_overdue}</TableCell>
                                        <TableCell align="center">{Number(row?.loanissued?.issuedamount).toLocaleString("en-US", { minimumFractionDigits: 2 })}</TableCell>
                                        <TableCell align="center">{row?.loanissued?.issueddate}</TableCell>
                                        <TableCell align="center">{row?.loanissued?.loanduedate}</TableCell>
                                        {/* <TableCell align="center">{row?.loanissued?.disbursementamount}</TableCell> */}
                                        <TableCell align="center">{Number(row?.loanissued?.outstandingprinciple).toLocaleString("en-US", { minimumFractionDigits: 2 })}</TableCell>
                                        <TableCell align="center">{Number(row?.loanissued?.outstandinglatefees).toLocaleString("en-US", { minimumFractionDigits: 2 })}</TableCell>
                                        <TableCell align="center">{Number(row?.loanissued?.totalamount).toLocaleString("en-US", { minimumFractionDigits: 2 })}</TableCell>
                                        <TableCell align="center">{Number(row?.loanissued?.totalamountapplied).toLocaleString("en-US", { minimumFractionDigits: 2 })}</TableCell>
                                    </TableRow>
                                ))
                                :
                                <TableRow>
                                    <TableCell align="center" colSpan={9}>
                                        <Typography variant="body1" color="textSecondary">
                                            No data available
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            }
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={ageAnalysisData.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Box>
        </Box>
    )
}

export default LoanUserAgeAnalysisTab;